import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { EyeIcon } from 'lucide-react';
import type { Product } from '@/features/products/types/products.types';

interface Props {
    data: Product[];
    isLoading?: boolean;
    canViewProduct: boolean;
}

/**
 * Products owned by a dealer, rendered with the shared Table (name, status and
 * a View action) to match the other dealer sub-tab data tables. The shared
 * Table renders its own empty state.
 */
export const DealerProductDataTable: React.FC<Props> = ({ data, isLoading = false, canViewProduct }) => {
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<Product>[]>(() => {
        const baseColumns: ColumnDef<Product>[] = [
            {
                accessorKey: 'name',
                header: 'Name',
                cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
            },
            {
                accessorKey: 'status_key',
                header: 'Status',
                cell: (info) => <span className="text-sm text-mp-muted">{(info.getValue() as string) ?? '—'}</span>,
            },
        ];

        if (!canViewProduct) {
            return baseColumns;
        }

        return [
            ...baseColumns,
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: (info) => {
                    const product = info.row.original;
                    return (
                        <Tooltip content="View product">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/products/${product.id}`)}
                                color="primary"
                                ariaLabel={`View ${product.name}`}
                                startIcon={<EyeIcon size={20} />}
                            />
                        </Tooltip>
                    );
                },
            },
        ];
    }, [navigate, canViewProduct]);

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading..." />;
    }

    return <Table<Product> data={data} columns={columns} enableSorting={false} emptyMessage="No products owned by this dealer yet." />;
};
